<template>
  <AppSplash :condition="waitingAuthUser">
    <template v-slot:app-content>
      <v-app>
        <AppDialog
          v-if="!$store.state.maintenanceMode"
          :dialog="$store.state.dialog"
        />
        <PendingDialog
          v-if="pendingDialog && pendingShown"
          @close="
            () => {
              pendingDialog = false
            }
          "
        />

        <CertificateDialog
          v-if="isAdminUser"
          :certificates="certificatesExpiration"
        />

        <NewsDialog v-if="urgentNews.length > 0" :news="urgentNews" />
        <AppBar
          v-if="$route.name !== 'LoginView' && !$store.state.maintenanceMode"
        />
        <AppNavigationDrawer />
        <v-main :style="{ 'padding-right': isMobile ? '0px' : undefined }">
          <AppProgress
            v-if="$route.name !== 'LoginView'"
            :items="$store.state.progress"
            @setAbsentUser="(newUser) => setAbsentUser(newUser)"
          />
          <MaintenanceView v-if="$store.state.maintenanceMode" />
          <NotFoundView v-else-if="$store.state.notFound" />
          <router-view v-else :key="componentKey" />
        </v-main>
        <AppFooter
          v-if="$route.name !== 'LoginView' && !$store.state.maintenanceMode"
        />
        <AppSnackbar :snackbar="$store.state.snackbar" />
      </v-app>
    </template>
  </AppSplash>
</template>

<script>
import { getAppMetadataSubscription } from '@/services/metadata-service'
import { getAppLegalSubscription } from '@/services/legal-service'
import AppBar from '@/components/app-bar/AppBar'
import AppNavigationDrawer from '@/components/app-navigation-drawer/AppNavigationDrawer'
import NotFoundView from '@/views/NotFoundView'
import MaintenanceView from '@/views/MaintenanceView'
import AppFooter from '@/components/app-footer/AppFooter'
import AppSplash from '@/components/commons/AppSplash'
import AppSnackbar from '@/components/commons/AppSnackbar'
import AppDialog from '@/components/commons/AppDialog'
import AppProgress from '@/components/commons/AppProgress'
import NewsDialog from '@/components/news/NewsDialog'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { Auth } from '@/firebase-exports'
import { updateNotifications } from './services/notification-service'
import { updatePlan } from './services/plan-service'
import { hasToAuthorize } from './services/client-service'
import PendingDialog from '@/components/cifs/clients/PendingDialog'
import CertificateDialog from '@/components/certificate/CertificateDialog'
import { parentuserSubscription } from '@/services/parent-service'
import { getBilling } from '@/services/billing-service'
import { getMassiveActionsSettings } from './services/massive-actions-service'

export default {
  components: {
    AppBar,
    AppNavigationDrawer,
    NotFoundView,
    AppFooter,
    AppSplash,
    AppSnackbar,
    AppDialog,
    AppProgress,
    MaintenanceView,
    NewsDialog,
    PendingDialog,
    CertificateDialog,
  },
  watch: {
    waitingAuthUser: function () {
      this.setTabTitle()
    },
    '$i18n.locale': function () {
      this.setTabTitle()
    },
    '$route.fullPath': function () {
      this.setTabTitle()
    },
    '$store.state.notFound': function () {
      this.setTabTitle()
    },
    '$store.state.user': async function (newValue) {
      if (newValue && newValue.tipo === 'clienteDespacho') {
        if (
          (await hasToAuthorize(
            newValue.id,
            newValue.cifsRef,
            newValue.cifsPermissions
          )) &&
          this.$route.name !== 'CifsView'
        ) {
          this.pendingDialog = true
          this.pendingShown = true
        }
      }
      if (newValue && newValue.disabled === true) {
        const message = this.$t('userDisabled')
        this.setSnackbar({ position: 'top', type: 'error', message })
        await new Promise((resolve) => setTimeout(resolve, 5000))
        await this.logout()
        this.$router.push({ name: 'LoginView' })
      }
    },
    'parentUser.disabled': async function (newValue) {
      if (newValue === true) {
        const message = this.$t('userDisabled')
        this.setSnackbar({ position: 'top', type: 'error', message })
        await new Promise((resolve) => setTimeout(resolve, 5000))
        await this.logout()
        this.$router.push({ name: 'LoginView' })
      }
    },
  },
  data() {
    return {
      unsubscribeMetadata: () => {},
      unsubscribeLegal: () => {},
      parentUserUnsubscribe: () => {},
      waitingAuthUser: true,
      timeOut: null,
      pendingDialog: false,
      pendingShown: false,
      uploadCertificate: false,
      parentUser: undefined,
      inactivityTimer: null, // Temporizador de inactividad
      inactivityLimitTime: 0, // Tiempo límite de inactividad en minutos (30 minutos = 30 * 60 * 1000)
    }
  },
  methods: {
    ...mapMutations([
      'setDialog',
      'setSnackbar',
      'setProgress',
      'setNavigationDrawer',
    ]),
    ...mapActions(['logout', 'fetchUser', 'fetchAbsentUser']),
    userUnsubscribe() {},
    setTabTitle() {
      var tabTitle = this.$store.state.notFound
        ? this.$t('notFound')
        : this.$store.state.maintenanceMode
        ? this.$t('maintenanceTitle')
        : this.$t(`tabTitle.${this.$route.meta.tabTitle}`)
      if (tabTitle && !this.waitingAuthUser) tabTitle += ' - ' + 'GoodOK'
      else tabTitle = 'GoodOK'
      document.title = tabTitle
    },
    // Reiniciar el temporizador de inactividad
    async resetInactivityTimer() {
      clearTimeout(this.inactivityTimer) // Limpiar el temporizador anterior
      this.inactivityLimitTime = this.inactivityLimit * 60 * 1000 // Tiempo límite de inactividad
      this.inactivityTimer = setTimeout(async () => {
        if (this.$store.state.user) {
          this.setNavigationDrawer(false)
          await this.logout()
          this.$router.push({ name: 'LoginView' })
        }
      }, this.inactivityLimitTime)
    },
    // Monitorear actividad del usuario
    monitorUserActivity() {
      const resetTimer = this.resetInactivityTimer // Alias para acceder al método desde eventos
      window.addEventListener('mousemove', resetTimer)
      window.addEventListener('keydown', resetTimer)
      window.addEventListener('click', resetTimer)
      window.addEventListener('touchstart', resetTimer)

      // Inicializar el temporizador al montar el componente
      this.resetInactivityTimer()
    },
    async setAbsentUser(user) {
      if (
        // Si el usuario que ha hecho login se encuentra en alguna de las siguientes rutas y pulsa
        // sobre el boton de sustitución de otro usuario, redirigir antes al Dashboard
        [
          'SettingsView',
          'NotificationDetailView',
          'SharedNotificationDetailView',
          'OperatorsView',
          'GroupsView',
          'CifsView',
          'FiltersView',
          'AutomatedTasksView',
          'AnalyticsDashboardView',
          'HelpView',
          'LegalAdvice',
          'otrovalorfuturo',
        ].includes(this.$route.name)
      ) {
        this.$router.push({ name: 'DashboardView' })
      }

      try {
        if (this.userUnsubscribe) this.userUnsubscribe()

        this.userUnsubscribe = await this.fetchAbsentUser(user)
      } catch {
        const message = this.$t('genericError')
        this.setSnackbar({ position: 'top', type: 'error', message })

        await new Promise((resolve) => setTimeout(resolve, 3000))
      }
    },
  },
  computed: {
    ...mapGetters(['isMobile', 'isAdminUser', 'inactivityLimit']),
    urgentNews() {
      var context = this
      return this.$store.state.news.filter(function (el) {
        return (
          el.push && el.usersRead.indexOf(context.$store.state.user.id) == -1
        )
      })
    },
    componentKey() {
      // Si el usuario (ausente) cambia, obligamos a recargar la vista cambiando la prop key, que ahora tambien contiene el id de usuario
      return this.$store.state.user
        ? `${this.$route.fullPath}-${this.$store.state.user?.id}`
        : this.$route.fullPath
    },
    certificatesExpiration() {
      const user = this.$store.state.user

      if (!user || !this.isAdminUser) {
        return []
      }

      const result = []

      // 1. Certificado principal del usuario
      if (user.certificateExpiration) {
        result.push({
          name: user.empresa,
          certificateExpiration: user.certificateExpiration,
          userId: user.id,
          cif: user.numeroDocIdentidad,
        })
      }

      // 2. Certificados alternativos
      const alternativeCertificates = user.alternativeCertificates || []
      alternativeCertificates.forEach((certificate) => {
        if (certificate.expiration) {
          result.push({
            name: certificate.name,
            certificateExpiration: certificate.expiration,
            userId: user.id,
            cif: certificate.numeroDocIdentidad,
          })
        }
      })

      // 3. CIFs
      const cifs = user.cifs || []
      cifs.forEach((cif) => {
        if (cif.certificateExpiration && !cif.deletionDate) {
          // no mostrar certificados de empresas encoladas para ser borradas.
          result.push({
            name: cif.empresa,
            certificateExpiration: cif.certificateExpiration,
            userId: user.id,
            cif: cif.numeroDocIdentidad,
          })
        }
      })

      return result.sort((a, b) => {
        //const da = new Date(a.certificateExpiration).getTime()
        //const db = new Date(b.certificateExpiration).getTime()
        //return da - db

        // Defensivo por si acaso algun registro no tiene un dato valido
        const ta = a.certificateExpiration?.toMillis?.()
        const tb = b.certificateExpiration?.toMillis?.()
        // ASCendente: primero el que caduca antes
        if (!ta && !tb) return 0
        if (!ta) return 1
        if (!tb) return -1
        return ta - tb
      })
    },
  },
  async beforeCreate() {
    Auth.onAuthStateChanged(Auth.getAuth(), async (user) => {
      if (user) {
        try {
          this.userUnsubscribe = await this.fetchUser()
          //Esperamos a que tenga valor el state antes de continuar
          while (!this.$store.state.user) {
            await new Promise((resolve) => setTimeout(resolve, 100))
          }

          if (this.$store.state.user.disabled) {
            const message = this.$t('userDisabled')
            this.setSnackbar({ position: 'top', type: 'error', message })
            await new Promise((resolve) => setTimeout(resolve, 5000))
            await this.logout()
            this.$router.push({ name: 'LoginView' })
            return
          }
          // En caso de ser un operador, suscripción al user parentRef para comprobar si esta disabled desde el management app
          if (this.$store.state.user.parentRef) {
            this.parentUserUnsubscribe = await parentuserSubscription(
              this.$store.state.user.parentRef.id,
              this
            )
            while (!this.parentUser) {
              await new Promise((resolve) => setTimeout(resolve, 100))
            }
          }

          if (!this.$store.getters.isDemoEnv) {
            // Si el user parent esta disabled, no descargar nuevas notificaciones
            if (!this.parentUser?.disabled) {
              updateNotifications()
              updatePlan()
            }
          }

          await getBilling()
          await getMassiveActionsSettings()

          if (this.inactivityLimit > 0) {
            this.monitorUserActivity() // Iniciar monitoreo de actividad si está conectado
          }

          if (
            this.$route.name === 'LoginView' ||
            this.$route.fullPath === '/'
          ) {
            const finalRoute = this.$route.query.redirect || '/dashboard'
            this.$router.replace(finalRoute)
          }
        } catch {
          const message = this.$t('genericError')
          this.setSnackbar({ position: 'top', type: 'error', message })

          await new Promise((resolve) => setTimeout(resolve, 3000))
          this.logout()
        }
      } else {
        if (this.userUnsubscribe) this.userUnsubscribe()
        if (
          this.$route.name !== 'LoginView' &&
          this.$route.name !== 'SharedNotificationDetailView'
        ) {
          const currentPath = this.$route.fullPath
          const redirect = currentPath !== '/' ? currentPath : undefined
          this.$router.push({ name: 'LoginView', query: { redirect } })
        }
        clearTimeout(this.inactivityTimer) // Detener el temporizador si no hay usuario
      }

      this.waitingAuthUser = false
    })
  },
  mounted() {
    this.unsubscribeMetadata = getAppMetadataSubscription()
    this.unsubscribeLegal = getAppLegalSubscription()
  },
  destroyed() {
    if (this.unsubscribeMetadata) this.unsubscribeMetadata()
    if (this.unsubscribeLegal) this.unsubscribeLegal()
    if (this.parentUserUnsubscribe) this.parentUserUnsubscribe()
  },
  async created() {
    this.setTabTitle()
  },
}
</script>

<style lang="scss">
.subtitle {
  font-family: 'Roboto';
  font-weight: 400;
  font-size: 0.75rem;
  letter-spacing: 0.0333333333em;
}
.emojicountries {
  font-family: 'Twemoji Country Flags', 'Roboto';
}
</style>
